import { gas_natural_separation_efficiency } from "@/libs/calculations/gasPhasePVT";
import { Natural_Gas_Separation_Options_Enum } from "gql/api";
import type { XrefIntakes } from "@/composables/forms/formTypes";

export type IntakeData = {
  shaftPowerLimitStandardHP: number;
  shaftPowerLimitHighStrengthHP: number;
  standardShaftRatingRatio: number;
  highStrengthShaftRatingRatio: number;
  title: string;
};

export type NaturalGasSeparationParams = {
  topCasingInnerDiameterInches: number;
  bottomCasingInnerDiameterInches: number;
  primaryIntakeOuterDiameterInches: number;
  primaryIntakePortSquareInches: number;
  liquidFlowRateBBLD: number;
  gasFlowRateSCFD: number;
  liquidDensityLBMFT3: number;
  gasDensityLBMFT3: number;
  topCasingLengthFt: number;
  bottomOfIntakesMDFt: number;
  liquidSurfaceTensionDYCM: number;
  liquidSuperficialVelocityFTSEC: number;
  gasSuperficialVelocityFTSEC: number;
  bubbleRiseVerticalVelocityFTSEC: number;
  bubbleRiseRadialVelocityFTSEC: number;
};

// Intake has gas separator if eff1 and eff2 aren't equal to 0
export function intakeHasGasSeparator(eff1: number, eff2: number): boolean {
  return eff1 !== 0 && eff2 !== 0;
}

export function intakesGasSeparatorCount(intakes: XrefIntakes): number {
  return intakes.filter(({ r_intake }) =>
    intakeHasGasSeparator(r_intake?.eff1, r_intake?.eff2)
  ).length;
}

// Intake Nameplate HP HP_intake_np hp
// Operating RPM RPM_pump rpm
// Mix Specific Gravity S_mix SpG
// returns HP_intake hp
export function intake_bhp_required(
  intake_nameplate_hp: number,
  operating_rpm: number,
  s_mix: number
): number {
  const rpm_ratio = operating_rpm / 3500;
  const intake_bhp =
    intake_nameplate_hp * rpm_ratio * rpm_ratio * rpm_ratio * s_mix;
  return intake_bhp;
}

function getCasingParameters(
  topCasingInnerDiameterInches: number,
  bottomCasingInnerDiameterInches: number,
  topCasingLengthFt: number,
  bottomOfIntakesMDFt: number
): { casingInnerDiameterInches: number | null } {
  if (topCasingInnerDiameterInches && topCasingLengthFt > bottomOfIntakesMDFt) {
    return { casingInnerDiameterInches: topCasingInnerDiameterInches };
  } else if (bottomCasingInnerDiameterInches) {
    return { casingInnerDiameterInches: bottomCasingInnerDiameterInches };
  }
  return { casingInnerDiameterInches: null };
}

export const calculateNaturalGasSeparation = (
  params: NaturalGasSeparationParams,
  naturalGasSeparation: Natural_Gas_Separation_Options_Enum | undefined
): number => {
  // Early return if no intake
  if (!params.primaryIntakeOuterDiameterInches) return 0;

  // Determine which calculation method to use based on correlations
  const useAlhanati =
    naturalGasSeparation === Natural_Gas_Separation_Options_Enum.Alhanati;

  const { casingInnerDiameterInches } = getCasingParameters(
    params.topCasingInnerDiameterInches,
    params.bottomCasingInnerDiameterInches,
    params.topCasingLengthFt,
    params.bottomOfIntakesMDFt
  );

  if (!casingInnerDiameterInches) return 0;

  if (useAlhanati) {
    // Alhanti uses this
    return gas_natural_separation_efficiency(
      casingInnerDiameterInches,
      params.primaryIntakeOuterDiameterInches,
      params.liquidFlowRateBBLD,
      params.gasFlowRateSCFD,
      params.liquidDensityLBMFT3,
      params.gasDensityLBMFT3
    );
  } else {
    return calculateAlHassanSeparation(
      casingInnerDiameterInches,
      params.primaryIntakeOuterDiameterInches,
      params.primaryIntakePortSquareInches,
      params.liquidSuperficialVelocityFTSEC,
      params.bubbleRiseVerticalVelocityFTSEC,
      params.bubbleRiseRadialVelocityFTSEC
    );
  }
};

// static/Al-Hassan.Nat.sep.xlsx
function calculateAlHassanSeparation(
  casingInnerDiameterInches: number,
  primaryIntakeOuterDiameterInches: number,
  primaryIntakePortSquareInches: number,
  liquidSuperficialVelocityFTSEC: number,
  bubbleRiseVerticalVelocityFTSEC: number,
  bubbleRiseRadialVelocityFTSEC: number
): number {
  // H6
  const annulusAreaSquareInches =
    Math.PI * (casingInnerDiameterInches / 2) ** 2 -
    Math.PI * (primaryIntakeOuterDiameterInches / 2) ** 2;

  const separationEfficiency =
    1 -
    ((bubbleRiseRadialVelocityFTSEC / bubbleRiseVerticalVelocityFTSEC) *
      (primaryIntakePortSquareInches / annulusAreaSquareInches) +
      liquidSuperficialVelocityFTSEC / bubbleRiseVerticalVelocityFTSEC);

  return Math.max(0, Math.min(1, separationEfficiency));
}
